/**
 * 🌳 素材库类别级联选择器（环艺学姐专属）
 *
 * 功能：
 * - 支持 1~5 级分类级联选择
 * - 每级下拉显示完整路径，避免同名歧义
 * - 达到 5 级上限后后续级别置灰
 * - 支持只选父级（也可选任意层级作为终点）
 */

import React, { useMemo, useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const MAX_LEVEL = 5;

const T = {
  inputBg: 'var(--modal-input-bg)',
  border: 'var(--border-subtle)',
  text: 'var(--text-primary)',
  text2: 'var(--text-secondary)',
  muted: 'var(--text-muted)'
};

/**
 * 把扁平类别列表转成树
 */
export function buildCategoryTree(flatList) {
  const nodes = new Map();
  for (const c of flatList) {
    nodes.set(c.id, { ...c, children: [] });
  }
  const roots = [];
  for (const node of nodes.values()) {
    if (node.parent_id && nodes.has(node.parent_id)) {
      nodes.get(node.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

/**
 * 把嵌套的类别树拍平为数组（保持层级顺序）
 */
export function flattenCategoryTree(tree) {
  const flat = [];
  const walk = (nodes) => {
    for (const n of nodes) {
      const { children, ...rest } = n;
      flat.push(rest);
      if (children && children.length) walk(children);
    }
  };
  walk(tree || []);
  return flat;
}

/**
 * 计算某节点的完整路径名（用于下拉展示）
 */
export function computeCategoryPaths(flatList) {
  const map = new Map(flatList.map(c => [c.id, c]));
  const paths = new Map();
  for (const c of flatList) {
    const chain = [];
    let cur = c;
    const guard = new Set();
    while (cur && !guard.has(cur.id)) {
      guard.add(cur.id);
      chain.unshift(cur.name);
      cur = map.get(cur.parent_id);
    }
    paths.set(c.id, chain.join(' / '));
  }
  return paths;
}

const inputStyle = {
  width: '100%',
  padding: '8px',
  border: `1px solid ${T.border}`,
  borderRadius: '4px',
  fontSize: '13px',
  background: T.inputBg,
  color: T.text,
  boxSizing: 'border-box'
};

const CategoryTreeSelect = ({
  tree = [],
  paths = new Map(),
  value = null,
  onChange,
  disabled = false,
  placeholder = '请选择分类'
}) => {
  // 每一级已选的节点 id，value 为最终选中的节点 id
  const [selectedPath, setSelectedPath] = useState([]);

  // 外部 value 变化时同步内部路径（用于编辑/回显）
  useEffect(() => {
    if (value === null || value === undefined) {
      setSelectedPath([]);
      return;
    }
    const findPath = (nodes, targetId, acc = []) => {
      for (const n of nodes) {
        const next = [...acc, n];
        if (n.id === targetId) return next;
        const found = findPath(n.children || [], targetId, next);
        if (found) return found;
      }
      return null;
    };
    const found = findPath(tree, value);
    setSelectedPath(found || []);
  }, [value, tree]);

  // 当前每一级的候选节点
  const levelOptions = useMemo(() => {
    const options = [];
    let currentLevel = tree;
    for (const node of selectedPath) {
      options.push(currentLevel);
      currentLevel = node.children || [];
      if (!currentLevel.length) break;
    }
    // 只要还没选满 5 级，就多给一级空选项
    if (selectedPath.length < MAX_LEVEL) {
      options.push(currentLevel);
    }
    return options;
  }, [tree, selectedPath]);

  const handleLevelChange = (levelIndex, nodeId) => {
    const next = selectedPath.slice(0, levelIndex);
    if (nodeId) {
      const nodes = levelOptions[levelIndex] || [];
      const node = nodes.find(n => n.id === nodeId);
      if (node) next.push(node);
    }
    setSelectedPath(next);

    const last = next[next.length - 1];
    onChange(last ? last.id : null);
  };

  // 当前已选路径的文字摘要
  const summary = selectedPath.length
    ? selectedPath.map(n => n.name).join(' / ')
    : '';

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(levelOptions.length, MAX_LEVEL)}, 1fr)`, gap: '8px' }}>
        {levelOptions.map((nodes, idx) => {
          const isLocked = idx > 0 && !selectedPath[idx - 1];
          return (
            <div key={idx} style={{ position: 'relative' }}>
              <select
                value={selectedPath[idx]?.id || ''}
                disabled={disabled || isLocked}
                onChange={(e) => handleLevelChange(idx, e.target.value ? parseInt(e.target.value, 10) : null)}
                style={{
                  ...inputStyle,
                  opacity: isLocked ? 0.5 : 1,
                  cursor: isLocked ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="">{idx === 0 ? placeholder : `第 ${idx + 1} 级`}</option>
                {nodes.map(n => (
                  <option key={n.id} value={n.id}>
                    {paths.get(n.id) || n.name}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      {summary && (
        <div style={{
          marginTop: '6px',
          fontSize: '12px',
          color: T.text2,
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <ChevronDown size={12} />
          已选：{summary}
        </div>
      )}
    </div>
  );
};

export default CategoryTreeSelect;
